import React, { memo, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface TimeInputProps {
  value: string; // Format: "9:00 AM", "12:15 PM", etc.
  disabled?: boolean;
  label?: string;
  timeFormat?: 'AM' | 'PM' | 'both'; // New prop to control AM/PM options
  onChange: (time: string) => void;
}

// Generate time options in 15-minute increments
const generateTimeOptions = (timeFormat: 'AM' | 'PM' | 'both' = 'both') => {
  const options: string[] = [];
  for (let hour = 1; hour <= 12; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      if (timeFormat === 'both' || timeFormat === 'AM') {
        options.push(`${hour}:${minute === 0 ? '00' : minute} AM`);
      }
      if (timeFormat === 'both' || timeFormat === 'PM') {
        options.push(`${hour}:${minute === 0 ? '00' : minute} PM`);
      }
    }
  }

  // Add 11:59 PM as a special option for "end of day"
  if (timeFormat === 'both' || timeFormat === 'PM') {
    options.push('11:59 PM');
  }

  return options;
};

// Pre-generate time options for each format
const AM_TIME_OPTIONS = generateTimeOptions('AM');
const PM_TIME_OPTIONS = generateTimeOptions('PM');
const ALL_TIME_OPTIONS = generateTimeOptions('both');

const TimeInput: React.FC<TimeInputProps> = memo(({
  value,
  disabled = false,
  label,
  timeFormat = 'both',
  onChange
}) => {
  // Handle time change
  const handleTimeChange = (newValue: string) => {
    onChange(newValue);
  };
  
  // Get the appropriate time options based on the timeFormat prop
  const getTimeOptions = () => {
    switch (timeFormat) {
      case 'AM':
        return AM_TIME_OPTIONS;
      case 'PM':
        return PM_TIME_OPTIONS;
      default:
        return ALL_TIME_OPTIONS;
    }
  };
  
  // Memoize the SelectItems to prevent re-rendering
  const timeSelectItems = useMemo(() => (
    getTimeOptions().map((time) => (
      <SelectItem key={time} value={time}>{time}</SelectItem>
    ))
  ), [timeFormat]);
  
  return (
    <div className="flex items-center">
      {label && <div className="text-xs text-gray-500 mr-2 whitespace-nowrap">{label}</div>}
      <Select
        value={value}
        onValueChange={handleTimeChange}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 w-[120px] px-2 py-1 border rounded bg-background">
          <SelectValue placeholder="Select time" />
        </SelectTrigger>
        <SelectContent>
          {timeSelectItems}
        </SelectContent>
      </Select>
    </div>
  );
});

TimeInput.displayName = 'TimeInput';

export default TimeInput;
